import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { Clock, ChevronRight, BookOpen } from "lucide-react";
import Layout from "../components/Layout";
import SEOHead from "../components/SEOHead";
import RevealText from "../components/RevealText";
import { listPostsByLang } from "../data/blog";

/**
 * Bilingual blog index. Filters posts by current i18n language.
 */
export default function Blog() {
  const { i18n } = useTranslation();
  const lang = i18n.language?.startsWith("en") ? "en" : "es";
  const isEs = lang === "es";
  const [activeCategory, setActiveCategory] = useState("all");

  const posts = useMemo(() => listPostsByLang(lang), [lang]);
  const categories = useMemo(() => {
    const set = new Set(posts.map((p) => p.category));
    return ["all", ...Array.from(set)];
  }, [posts]);

  const visible = activeCategory === "all"
    ? posts
    : posts.filter((p) => p.category === activeCategory);

  const hero = posts[0];
  const rest = visible.filter((p) => p.slug !== hero?.slug);

  const metaTitle = isEs ? "Blog — guías, tips y educación sobre electricidad en Texas" : "Blog — Texas electricity guides, tips and education";
  const metaDesc = isEs
    ? "Artículos honestos y bilingües sobre cómo elegir tu plan, entender tu factura y bajar tu consumo en Texas."
    : "Honest, bilingual articles on how to pick your plan, read your bill and lower consumption in Texas.";

  const blogJsonLd = {
    "@context": "https://schema.org",
    "@type": "Blog",
    name: "Velcor Energy Blog",
    inLanguage: isEs ? "es-US" : "en-US",
    publisher: { "@type": "Organization", name: "Velcor Energy", url: "https://velcorenergy.com" },
    blogPost: posts.slice(0, 6).map((p) => ({
      "@type": "BlogPosting",
      headline: p.title,
      datePublished: p.publishedAt,
      author: { "@type": "Organization", name: p.author },
      url: `/blog/${p.slug}`,
    })),
  };

  return (
    <Layout>
      <SEOHead
        title={metaTitle}
        description={metaDesc}
        canonical="/blog"
        ogImage="/hero-day.jpg"
        jsonLd={blogJsonLd}
      />

      {/* Header */}
      <section className="max-w-[1180px] mx-auto px-5 pt-14 md:pt-20 pb-10">
        <div className="text-[11px] font-extrabold uppercase tracking-[0.22em] text-velcor-green mb-3">
          {isEs ? "Blog Velcor" : "Velcor Blog"}
        </div>
        <h1
          className="display text-velcor-ink leading-[1.04] max-w-[800px]"
          style={{ fontSize: "clamp(34px, 5.4vw, 60px)", fontWeight: 600, letterSpacing: "-0.022em" }}
        >
          <RevealText text={isEs ? "Energía honesta," : "Honest energy,"} />{" "}
          <RevealText text={isEs ? "explicada bien." : "well explained."} className="display-accent" delay={0.18} />
        </h1>
        <p className="mt-5 text-slate-500 text-[16px] max-w-[600px] leading-relaxed">
          {isEs
            ? "Guías, tips y educación sin gimmicks sobre cómo funciona el mercado eléctrico de Texas."
            : "Guides, tips and education without gimmicks on how Texas's electricity market actually works."}
        </p>

        {/* Category filter */}
        <div className="mt-9 flex flex-wrap gap-2" data-testid="blog-categories">
          {categories.map((c) => {
            const active = activeCategory === c;
            const label = c === "all" ? (isEs ? "Todos" : "All") : c;
            return (
              <button
                key={c}
                onClick={() => setActiveCategory(c)}
                data-testid={`blog-cat-${c}`}
                className={`px-4 py-2 rounded-full text-[12.5px] font-bold transition-all ${
                  active
                    ? "bg-velcor-ink text-white"
                    : "bg-white text-slate-500 border border-slate-200 hover:border-velcor-ink/40"
                }`}
              >
                {label}
              </button>
            );
          })}
        </div>
      </section>

      {/* Featured hero post */}
      {hero && activeCategory === "all" && (
        <section className="max-w-[1180px] mx-auto px-5 pb-12">
          <Link
            to={`/blog/${hero.slug}`}
            data-testid="blog-hero-post"
            className="group block bg-white rounded-3xl border border-slate-200 overflow-hidden hover:border-velcor-green transition-colors"
          >
            <div className="grid md:grid-cols-[1.2fr_1fr]">
              <div
                className="aspect-[16/10] md:aspect-auto bg-cover bg-center"
                style={{ backgroundImage: `url(${hero.cover})` }}
              />
              <div className="p-7 md:p-10 flex flex-col justify-center">
                <div className="flex items-center gap-3 text-[11px] font-extrabold uppercase tracking-[0.16em]">
                  <span className="text-velcor-green">{hero.category}</span>
                  <span className="text-slate-300">·</span>
                  <span className="text-slate-400 inline-flex items-center gap-1">
                    <Clock className="w-3 h-3" /> {hero.readTime} min
                  </span>
                </div>
                <h2
                  className="mt-4 display text-velcor-ink leading-tight group-hover:text-velcor-green transition-colors"
                  style={{ fontSize: "clamp(22px, 2.8vw, 32px)", fontWeight: 600 }}
                >
                  {hero.title}
                </h2>
                <p className="mt-4 text-slate-500 text-[14.5px] leading-relaxed">{hero.excerpt}</p>
                <div className="mt-6 inline-flex items-center gap-1 text-velcor-green font-bold text-[13px]">
                  {isEs ? "Leer artículo" : "Read article"} <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </div>
              </div>
            </div>
          </Link>
        </section>
      )}

      {/* Article grid */}
      <section className="max-w-[1180px] mx-auto px-5 pb-20">
        {visible.length === 0 ? (
          <div className="text-center py-16 text-slate-400">
            <BookOpen className="w-10 h-10 mx-auto mb-3 opacity-40" />
            {isEs ? "No hay artículos en esta categoría aún." : "No articles in this category yet."}
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-7" data-testid="blog-grid">
            {(activeCategory === "all" ? rest : visible).map((p, idx) => (
              <motion.article
                key={p.slug}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.5, delay: idx * 0.05 }}
              >
                <Link
                  to={`/blog/${p.slug}`}
                  data-testid={`blog-card-${p.slug}`}
                  className="group block bg-white rounded-3xl border border-slate-200 overflow-hidden hover:border-velcor-green transition-colors h-full"
                >
                  <div
                    className="aspect-[16/10] bg-cover bg-center"
                    style={{ backgroundImage: `url(${p.cover})` }}
                  />
                  <div className="p-6">
                    <div className="flex items-center gap-3 text-[10.5px] font-extrabold uppercase tracking-[0.16em]">
                      <span className="text-velcor-green">{p.category}</span>
                      <span className="text-slate-300">·</span>
                      <span className="text-slate-400 inline-flex items-center gap-1">
                        <Clock className="w-3 h-3" /> {p.readTime} min
                      </span>
                    </div>
                    <h3
                      className="mt-3 text-velcor-ink font-bold leading-tight group-hover:text-velcor-green transition-colors"
                      style={{ fontSize: 17 }}
                    >
                      {p.title}
                    </h3>
                    <p className="mt-3 text-slate-500 text-[13.5px] leading-relaxed line-clamp-3">{p.excerpt}</p>
                  </div>
                </Link>
              </motion.article>
            ))}
          </div>
        )}
      </section>
    </Layout>
  );
}
